import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Box, Button, type ButtonProps, Card, Stack, TextField, Typography, styled } from "~/common/mui";

import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import { ContractItemParam, QuoteItemParam } from "~/features/cart/cart/components/LineItemStaticParams";
import { Price as PriceV3 } from "~/domains/pricing/components/Price/v3/Price";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { Parameter } from "~/common/components/ParameterV2";
import { getEDDLabel } from "~/features/cart/cart/utils/cartUtils";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import type { LineItemActions } from "~/domains/cart/hooks/useLineItemActions";
import { LineItemMessagesSection } from "~/domains/cart/components";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { type PriceData } from "~/domains/pricing/types/PriceData";

import { useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";

import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { LineItemStockIndicator } from "../cart/components/LineItemStockIndicator";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { EditablePackagingCell } from "../cart/components/PackagingCell";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import Delete from "~/common/mui/icons/Delete";
import { ToggleCommentButton } from "./ToggleCommentButton";
import {
  IncludedSurchargeAsterisk,
  TariffSurchargeAsterisk,
  OptionalBox,
  CurrentPrice,
  OldPrice,
} from "./commonComponents";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { ProductLineGrid } from "./ProductLineGrid";
import { KitPartsSection, type ResolvedOptionalPart } from "./components/KitPartsSection";
import type { ProductLineViewModel } from "./models/ProductTableViewModel";
import { OptionalPartEntry } from "./OptionalPartEntry";
import { useLineItemActions } from "~/domains/cart/hooks";
import CustomNumberFormat, { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import { useCallback, useState } from "react";
import { useSession } from "~/app/providers/SessionProvider";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { LinkButton } from "~/common/components/Link";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { MUIDateInput } from "~/common/components/MUIDateInput";

interface LineItemComponentProps {
  className?: string;
  line: ProductLineViewModel;
  product: ProductDescriptor;
  actions: LineItemActions;
  housingKit?: boolean;
  onDeleteLine?: () => void;
}

const DeleteButton = (props: ButtonProps) => {
  return <Button variant="text" color="textPrimary" startIcon={<Delete />} {...props} />;
};

const EditableKitPartsSection = ({ kit }: { kit: NonNullable<ProductLineViewModel["kit"]> }) => (
  <KitPartsSection
    kit={kit}
    renderOptionalPart={(part) => <EditableOptionalPartWrapper key={part.rowId} part={part} />}
  />
);

export const CartProductLine = ({
  className,
  line,
  product,
  actions,
  housingKit,
  onDeleteLine,
}: LineItemComponentProps) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();

  const { pricing, validation, flags, messages, kit, phaseoutLocked } = line;
  const lineItem = line.sourceLineItem as LineItem;
  const { isOnSale, saleActive, salesStock } = pricing;
  const { quantityVisible, isPMA } = flags;
  const { disableEditing } = validation;

  const netPrice = pricing.netPrice;
  const tariffSurcharge = pricing.tariffSurcharge;
  const surchargeValue = pricing.surchargeValue;
  const price = pricing.displayPrice ?? netPrice;
  const oldPrice = pricing.displayOldPrice ?? undefined;
  const discountFormatted = pricing.discountFormatted ?? undefined;
  const zeroText = pricing.zeroPriceLabel
    ? t(pricing.zeroPriceLabel.key, {
        ...pricing.zeroPriceLabel.params,
        defaultValue: pricing.zeroPriceLabel.fallback,
      })
    : undefined;
  const lineItemIsFromContract = !!lineItem.contractNumber;

  const totalPrice = pricing.totalPrice;
  const onSaleProduct = useSalesHelper(product).isProductOnSale();
  const targetPriceValue =
    typeof lineItem.targetPrice === "number" ? lineItem.targetPrice : lineItem.targetPrice?.value;
  const hasTargetPrice = targetPriceValue !== undefined && targetPriceValue !== null && targetPriceValue > 0;
  // Target Price Enable Check
  const targetPriceEnabled = useSession()?.commerceContext.targetPriceEnabled ?? false;

  const hasInvalidTargetPrice =
    targetPriceEnabled && targetPriceValue !== undefined && targetPriceValue !== null && targetPriceValue <= 0;

  const targetPriceErrorCode = hasInvalidTargetPrice ? "bulkUpload.error.targetPrice.invalid" : undefined;
  const isTargetPriceInvalid = !!targetPriceErrorCode;
  const targetPriceHelperText = targetPriceErrorCode ? t(targetPriceErrorCode) : null;

  const onTargetPriceChange = useCallback(
    (evt: NumberFormatEvent) => {
      const isBlank =
        evt.value === undefined || evt.value === null || (typeof evt.value === "string" && evt.value.trim() === "");
      const newValue = isBlank ? undefined : Number(evt.value);
      const currencyIso = netPrice?.currencyIso ?? price?.currencyIso;
      const priceData: PriceData | undefined =
        currencyIso && newValue !== undefined ? { value: newValue, currencyIso } : undefined;
      actions.updateCartTargetPrice(priceData);
    },
    [actions],
  );

  const [showTargetPriceInput, setShowTargetPriceInput] = useState(hasTargetPrice);
  const toggleTargetPrice = useCallback(() => setShowTargetPriceInput((prev) => !prev), []);

  const showPriceReferenceSlot = canSeePriceRefColumn(selection) || isOnSale;

  const moqHelperLabel = validation.MOQHelperText
    ? t(validation.MOQHelperText.key, {
        ...validation.MOQHelperText.params,
        defaultValue: validation.MOQHelperText.fallback,
      })
    : undefined;

  const aliases = ProductParams2.ProductAliases({
    mode: "15-digits",
    visibleAliasesLayout: "separate-rows",
    packCode: lineItem.packCode,
  })({
    t,
    product,
  });

  const gpsd = ProductParams2.GPSD({
    t,
    product,
  });

  const isMinDesktopM = useIsMinDesktopSizeM();

  const [commentRef, toggleComment] = useShowAndScrollInput(actions.toggleShowComment);

  return (
    <Card sx={{ p: 3 }} component={Stack} gap={3} className={className}>
      <ProductLineGrid>
        <ProductLineGrid.StockIndicator>
          <LineItemStockIndicator product={product} packEntry={lineItem} />
        </ProductLineGrid.StockIndicator>

        <ProductLineGrid.Quantity>
          <Stack gap={1}>
            <QuantityInput
              variant="compact"
              value={lineItem.quantity}
              step={validation.minQuantityValue}
              onValueChange={actions.updateQty}
              error={validation.quantityHasError}
              helperLabel={moqHelperLabel}
              disabled={disableEditing || phaseoutLocked}
              min={0}
              label={false}
            />
            {salesStock > 0 && saleActive && <SaleQuantityIndicator.V2 quantity={salesStock} />}
          </Stack>
        </ProductLineGrid.Quantity>

        <ProductLineGrid.NetPrice>
          {!showTargetPriceInput && netPrice && (
            <Parameter
              label={<PriceElements.NetPriceLabel variant="inherit" price={netPrice} />}
              value={
                <PriceElements.Stack gap={0}>
                  <Stack direction="row" gap={1}>
                    <CurrentPrice
                      price={price}
                      zeroText={zeroText}
                      tariffSurcharge={tariffSurcharge}
                      variant="inherit"
                    />
                    {discountFormatted && (
                      <SaleDiscountIndicator.V2 variant="bodyMedium" discountFormatted={discountFormatted} />
                    )}
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} variant="bodySmall" tariffSurcharge={tariffSurcharge} />}
                  {!saleActive && (
                    <Typography variant="bodySmall">
                      <PriceV3.ExtraLabels price={netPrice} surchargeEnabled={false} />
                    </Typography>
                  )}
                </PriceElements.Stack>
              }
            />
          )}

          {/* Target Price Input Field */}
          {!onSaleProduct && showTargetPriceInput && targetPriceEnabled && (
            <CustomNumberFormat
              data-test-id={"bulk-upload-item-targetPrice"}
              value={targetPriceValue}
              decimalScale={2}
              label={t("cartPage.table.header.targetPrice.title")}
              handleState={onTargetPriceChange}
              error={isTargetPriceInvalid}
              helperText={targetPriceHelperText}
            />
          )}

          {/* Show net price / Enter target price button text */}
          {!lineItemIsFromContract &&
            !lineItem.quoteNumber &&
            !kit &&
            !housingKit &&
            !onSaleProduct &&
            targetPriceEnabled && (
              <LinkButton onClick={toggleTargetPrice} style={{ marginTop: "4px" }}>
                {showTargetPriceInput
                  ? t("cart.page.show.net.price.text")
                  : t("cart.page.show.enter.target.price.text")}
              </LinkButton>
            )}
        </ProductLineGrid.NetPrice>

        {totalPrice && (
          <ProductLineGrid.TotalPrice>
            <Parameter
              label={<PriceElements.TotalPriceLabel variant="inherit" price={totalPrice} />}
              value={
                <CurrentPrice price={totalPrice} variant="inherit" tariffSurcharge={tariffSurcharge} surchargeEnabled />
              }
            />
          </ProductLineGrid.TotalPrice>
        )}

        <ProductLineGrid.DeliveryDate>
          {quantityVisible && !isPMA ? (
            <MUIDateInput
              label={t("cartPage.table.header.delivery.title")}
              helperLabel={getEDDLabel(lineItem, t)}
              date={
                lineItem.deliveryDate ? (lineItem.manuallySetRDD ? lineItem.deliveryDate : lineItem.deliveryDate) : null
              }
              onChange={actions.updateDate}
              onClear={actions.clearDate}
              showClear={lineItem.manuallySetRDD}
              isInvalid={!lineItem.deliveryDate}
              isDayDisabled={actions.isDayDisabled}
              disabled={disableEditing}
            />
          ) : (
            <div>-</div>
          )}
        </ProductLineGrid.DeliveryDate>

        <ProductLineGrid.Packaging>
          <EditablePackagingCell
            product={product}
            lineItem={lineItem}
            onChange={actions.selectPackage}
            phaseoutLocked={phaseoutLocked}
          />
        </ProductLineGrid.Packaging>

        {showPriceReferenceSlot && (
          <ProductLineGrid.PriceReference>
            <Stack>
              <PriceReferenceParam
                product={product.base}
                priceReference={lineItem.priceReference}
                onChange={actions.updatePriceReference}
                data-test-id="package-entry-price-reference"
                disabled={disableEditing || saleActive}
                editable={!lineItem.quoteNumber && canSeePriceRefColumn(selection)}
                size={"medium"}
              />
              <CheckboxWithLabel
                checked={saleActive}
                onCheck={actions.updateSalesEnabled}
                hidden={!isOnSale || !!lineItem.priceReference}
              >
                {t("feature.salesCampaign.specialDealPrice")}
              </CheckboxWithLabel>
            </Stack>
          </ProductLineGrid.PriceReference>
        )}

        {aliases && (
          <ProductLineGrid.Aliases>
            <Parameter {...aliases} />
          </ProductLineGrid.Aliases>
        )}

        {gpsd && (
          <ProductLineGrid.GPSD>
            <Parameter {...gpsd} />
          </ProductLineGrid.GPSD>
        )}

        <ProductLineGrid.QuoteDetails>
          <QuoteItemParam product={product.base} lineItem={lineItem} />
          <ContractItemParam product={product.base} lineItem={lineItem} />
        </ProductLineGrid.QuoteDetails>

        {isLineLevelCustomerOrderNumberEnabled() && (
          <ProductLineGrid.PONumber>
            <TextField
              fullWidth
              label={t("search.type.customerOrderNumber")}
              inputProps={{ "data-test-id": "package-entry-po-number" }}
              value={lineItem.customerOrderNumber}
              onChange={actions.updatePONumber}
            />
          </ProductLineGrid.PONumber>
        )}

        <ProductLineGrid.Messages>
          <LineItemMessagesSection
            messages={messages}
            isPMA={isPMA}
            isOptime={flags.isOptime}
            quantityVisible={quantityVisible}
          />
        </ProductLineGrid.Messages>
      </ProductLineGrid>

      <OptionalBox>
        {surchargeValue && <IncludedSurchargeAsterisk value={surchargeValue} percent={lineItem.netPrice?.surcharge} />}
        {tariffSurcharge && <TariffSurchargeAsterisk />}
      </OptionalBox>

      {kit && <EditableKitPartsSection kit={kit} />}

      <PartialDeliveriesSection partialDeliveries={lineItem.partialDeliveries} />

      <Stack direction={"row"} justifyContent={"space-between"}>
        <ToggleCommentButton open={lineItem.showComments} onClick={toggleComment} />
        <DeleteButton
          onClick={onDeleteLine}
          disabled={!onDeleteLine || (lineItem.isBundled && lineItem.original) || phaseoutLocked}
        >
          {t("cart.deleteLine")}
        </DeleteButton>
      </Stack>

      {lineItem.showComments && (
        <Box sx={{ width: { phone: "100%", "desktop-m": "50%" } }}>
          <CommentInput
            value={lineItem.comment || ""}
            onChange={actions.updateComment}
            ref={commentRef}
            label={t("wishlistPage.comment.label")}
          />
        </Box>
      )}
    </Card>
  );
};

const EditableOptionalPartLine = styled(CartProductLine)(({ theme }) => ({
  border: "none",
  borderTop: "1px solid",
  borderRadius: 0,
  borderColor: theme.palette.divider,
}));

function EditableOptionalPartWrapper({ part }: { part: ResolvedOptionalPart }) {
  const actions = useLineItemActions({ lineItem: part.sourceLineItem, product: part.descriptor });
  return (
    <OptionalPartEntry descriptor={part.descriptor} editMode={true}>
      <EditableOptionalPartLine housingKit={true} line={part.viewModel} product={part.descriptor} actions={actions} />
    </OptionalPartEntry>
  );
}
